import {
  _storeEntityDataListToLocalForage,
  getEntityListDataFromLocalForage,
} from '../../modules/mockDataStorageManager'
import {
  READ_STATUS,
  inReplyToRefusal,
  readStatusChangeRefusal,
  readStatusItem,
} from '../../modules/emailReadStatusRules'
import type { ICaseEmail } from '~/types/case/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/api/caseEmails/mockData.ts]'

/**
 * Case emails: the envelope, the transport, and -- since cynque-api-server
 * #1737 -- the readable `body`, which used to live on a CaseNote the email
 * pointed at (`caseNoteId`).
 *
 * ⚠ Hand-written rather than built with makeNoteMockData / makeDocumentMockData,
 * because ICaseEmail is deliberately NOT an entity -- no version, no hrid, no
 * canBeDeleted. An arrived message is append-only and never edited, so the
 * concurrency and CRUD machinery those factories assume would serve mutations
 * that do not happen.
 */

/**
 * The seed row, with the mock's own bookkeeping.
 *
 * ⚠ `_caseId` is NOT on the wire. `case_emails` has a real case column
 * server-side and the overview view groups BY it, so the case is implicit in the
 * payload and `CaseEmail` carries no case field. Keeping it underscored and
 * stripping it on the way out means the mock cannot end up WIDER than the server
 * -- the mirror of the narrower-than-the-server trap, and just as good at hiding
 * a defect.
 */
type ICaseEmailSeed = ICaseEmail & { _caseId: TEntityId }

export async function getStartingCaseEmailListData(): Promise<
  ICaseEmailSeed[]
> {
  return [
    // The customer's original message.
    {
      _caseId: 'XXCA0001',
      id: 'XXCAE0001',
      direction: 'inbound',
      body:
        'Since Monday the Boston line drops about one call in five, usually ' +
        'after a minute or two. Happens on all four handsets. Invoice and a ' +
        'photo of the NIU attached.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'POTS line at Boston dropping calls',
      fromAddress: 'dana.whitfield@northwindlogistics.com',
      fromName: 'Dana Whitfield',
      toAddresses: ['cases@wxtechnologygroup.com'],
      ccAddresses: ['ops@northwindlogistics.com'],
      replyTo: null,
      sentAt: '2022-03-01T14:02:11Z',
      receivedAt: '2022-03-01T14:02:47Z',
      createdAt: '2022-03-01T14:02:47Z',
      messageId: '<CAF1a9c2@northwindlogistics.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-8f2c14aa',
      mailboxHash: 'case-XXCA0001',
      spamScore: 0.4,
      // Answered by XXCAE0002.
      readStatus: readStatusItem(READ_STATUS.REPLIED),
    },
    // The agent's reply -- and it BOUNCED. This is the row the UI must not hide:
    // there is no notification mechanism behind deliveryState, so a case that
    // swallows this leaves the agent believing they replied while the customer
    // believes they were ignored.
    {
      _caseId: 'XXCA0001',
      id: 'XXCAE0002',
      direction: 'outbound',
      body:
        'Thanks Dana. We are dispatching a technician for Thursday morning ' +
        'and have opened a trouble ticket with the carrier in the meantime.',
      sentBy: { id: 'XXTM0006', label: 'Service Technician' },
      repeatOf: null,
      deliveryState: 'bounced',
      subject: 'Re: POTS line at Boston dropping calls',
      fromAddress: 'cases@wxtechnologygroup.com',
      fromName: 'WX Technology Group Support',
      toAddresses: ['dana.whitfield@northwindlogistics.com'],
      ccAddresses: null,
      replyTo: 'cases@wxtechnologygroup.com',
      sentAt: '2022-03-01T16:40:00Z',
      receivedAt: null,
      createdAt: '2022-03-01T16:39:52Z',
      messageId: '<9d31be04@wxtechnologygroup.com>',
      inReplyTo: '<CAF1a9c2@northwindlogistics.com>',
      messageReferences: ['<CAF1a9c2@northwindlogistics.com>'],
      providerMessageId: 'pm-77b90c31',
      mailboxHash: 'case-XXCA0001',
      spamScore: null,
      // Outbound: no read status.
      readStatus: null,
    },
    // ⚠ body NULL: an out-of-office auto-reply. Logged, but deliberately not
    // surfaced as readable text -- a null body is exactly what keeps
    // auto-replies, bounce notifications and over-threshold spam off the Emails
    // card. The overview DOES return it, so the card must filter it out.
    {
      _caseId: 'XXCA0001',
      id: 'XXCAE0003',
      direction: 'inbound',
      body: null,
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Automatic reply: POTS line at Boston dropping calls',
      fromAddress: 'dana.whitfield@northwindlogistics.com',
      fromName: 'Dana Whitfield',
      toAddresses: ['cases@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-01T16:40:30Z',
      receivedAt: '2022-03-01T16:40:58Z',
      createdAt: '2022-03-01T16:40:58Z',
      messageId: '<ooo-5521@northwindlogistics.com>',
      inReplyTo: '<9d31be04@wxtechnologygroup.com>',
      messageReferences: ['<CAF1a9c2@northwindlogistics.com>'],
      providerMessageId: 'pm-1c0d5e77',
      mailboxHash: 'case-XXCA0001',
      spamScore: 1.1,
      // Opened, never answered (an auto-reply).
      readStatus: readStatusItem(READ_STATUS.READ),
    },
    // A second correspondent, on XXCA0003, whose address is SUPPRESSED. Exists so
    // the reply endpoint's 409 can actually occur locally: the server refuses a
    // reply to an address Postmark has suppressed after a bounce, and a mock that
    // cannot produce that leaves the whole recovery path untestable.
    //
    // ⚠ XXCA0002 deliberately keeps NO emails -- it is the control for "a case
    // opened in the app has nobody to reply to", which is a different refusal
    // (400) and must stay distinguishable from this one.
    {
      _caseId: 'XXCA0003',
      id: 'XXCAE0004',
      direction: 'inbound',
      body:
        'Power came back around 6am but the line has been dead since. ' +
        'Nothing on the handset at all.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'No dial tone after the power cut',
      fromAddress: SUPPRESSED_ADDRESS,
      fromName: 'Ray Kowalski',
      toAddresses: ['cases@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-06T09:14:00Z',
      receivedAt: '2022-03-06T09:14:31Z',
      createdAt: '2022-03-06T09:14:31Z',
      messageId: '<pwr-3312@brightmill.example>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-40ab19c2',
      mailboxHash: 'case-XXCA0003',
      spamScore: 0.2,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
    // ⚠ THE MESSAGE THAT CREATED XXCA0005. `inReplyTo` and `messageReferences`
    // are BOTH null, which is what makes it a case-opening message rather than
    // a reply into an existing thread -- the pathway had no thread to join.
    // Its sender resolves to a known contact, so the case it opened carries one.
    {
      _caseId: 'XXCA0005',
      id: 'XXCAE0005',
      direction: 'inbound',
      body:
        'Every call out of the Sunset office has heavy static from about ' +
        'ten seconds in. Started yesterday afternoon. Six people affected, ' +
        'all on the same floor.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Static on every call from the Sunset office',
      fromAddress: 'bilbo@bostoncoach.com',
      fromName: 'Bilbo Baggins',
      toAddresses: ['cases@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-12T09:14:51Z',
      receivedAt: '2022-03-12T09:15:22Z',
      createdAt: '2022-03-12T09:15:22Z',
      messageId: '<b2f7ce10@bostoncoach.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-3ae0b912',
      mailboxHash: 'case-XXCA0005',
      spamScore: 0.2,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
    // The message that created XXCA0006, from an address no contact owns. The
    // envelope still carries the sender's name; the CASE carries no contact,
    // because a name on an envelope is not a record in the system.
    {
      _caseId: 'XXCA0006',
      id: 'XXCAE0006',
      direction: 'inbound',
      body:
        'We have moved. Could you update the billing address to 44 Cornhill, ' +
        'Suite 3, Boston MA 02108 before the next invoice run?',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Please update the billing address on our account',
      fromAddress: 'accounts@quarrystonepartners.com',
      fromName: 'Elaine Brooker',
      toAddresses: ['cases@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-14T16:40:35Z',
      receivedAt: '2022-03-14T16:41:03Z',
      createdAt: '2022-03-14T16:41:03Z',
      messageId: '<7d41aa93@quarrystonepartners.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-5b1e7fc4',
      mailboxHash: 'case-XXCA0006',
      spamScore: 0.9,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
  ]
}

/**
 * The one address the mock treats as suppressed, and the record the API returns
 * with its 409. Postmark suppresses an address after a hard bounce or a spam
 * complaint and then refuses further mail to it at their end -- which is why
 * `sendToSuppressed` records an attempt rather than delivering one.
 */
export const SUPPRESSED_ADDRESS = 'ray.kowalski@brightmill.example'

export const SUPPRESSION_RECORD = {
  recipient: SUPPRESSED_ADDRESS,
  event: 'HardBounce',
  bounceType: 'HardBounce',
  canReactivate: true,
  occurredAt: '2022-02-11T08:03:00Z',
}

export function findCaseEmailSuppression(address: string | null | undefined) {
  // Case-insensitive: the server matches on a lowered address, and a
  // differently-cased one must still match (its own integration test says so).
  return address && address.toLowerCase() === SUPPRESSED_ADDRESS.toLowerCase()
    ? SUPPRESSION_RECORD
    : undefined
}

/**
 * The server's 409 for suppressed recipients (services/email_composition.py
 * suppressed_problem): ProblemDetails with its `extra` flattened into the top
 * level -- the first suppression's fields, plus `suppressed` listing every one.
 * Shared by a case reply and an activity email's send.
 */
export function suppressedRecipientsBody(
  suppressions: NonNullable<ReturnType<typeof findCaseEmailSuppression>>[]
) {
  return {
    status: 409,
    title: 'Recipient address is suppressed',
    detail:
      suppressions
        .map(
          (s) =>
            `${s.recipient} was suppressed after a ${s.bounceType}, ` +
            'so Postmark will refuse this message.'
        )
        .join(' ') +
      ' It can be reactivated in Postmark, but consider finding another ' +
      'way to reach ' +
      (suppressions.length === 1 ? 'this contact.' : 'these contacts.') +
      ' Send anyway with sendToSuppressed.',
    ...suppressions[0],
    suppressed: suppressions,
  }
}

const STORAGE_KEY = 'caseemails'

let caseEmailListData: ICaseEmailSeed[] | undefined

/**
 * A bespoke loader rather than getListDataFunctionFactory, and the reason is
 * measured rather than stylistic.
 *
 * That factory is bounded on TListDataTypes, every member of which carries
 * IEntityDataItem's `[propName: string]: any` index signature. ICaseEmail has no
 * index signature -- it is a closed, hand-listed shape, deliberately not an
 * entity -- so adding it to that union strips the index signature from the union
 * as a whole and breaks ~25 unrelated mock modules that read arbitrary
 * properties off it. Tried it: the ratchet went 3099 -> 3129. Twenty lines here
 * is much the cheaper side of that trade.
 */
export async function getCaseEmailListData(): Promise<ICaseEmailSeed[]> {
  const fn = `${mn} getCaseEmailListData |`
  if (caseEmailListData) {
    return caseEmailListData
  }
  const fromStorage = await getEntityListDataFromLocalForage<ICaseEmailSeed[]>(
    STORAGE_KEY
  )
  if (fromStorage) {
    caseEmailListData = fromStorage.map(withReadStatus)
    return caseEmailListData
  }
  caseEmailListData = await getStartingCaseEmailListData()
  await _storeEntityDataListToLocalForage({
    entityKeyBase: STORAGE_KEY,
    listData: caseEmailListData as never,
    callingFn: fn,
  })
  return caseEmailListData
}

/**
 * Mock data saved before cynque-api-server #1814 has no `readStatus`. The
 * server's backfill makes every existing inbound message Read (or Replied), so
 * a saved inbound row without one reads as Read; anything else, null. An ADDED
 * key, so renameLegacyPersistedMockKeys.ts has no part in it.
 */
function withReadStatus(caseEmail: ICaseEmailSeed): ICaseEmailSeed {
  if ('readStatus' in caseEmail) {
    return caseEmail
  }
  return {
    ...caseEmail,
    readStatus:
      caseEmail.direction === 'inbound'
        ? readStatusItem(READ_STATUS.READ)
        : null,
  }
}

async function storeCaseEmails(list: ICaseEmailSeed[], callingFn: string) {
  await _storeEntityDataListToLocalForage({
    entityKeyBase: STORAGE_KEY,
    listData: list as never,
    callingFn,
  })
}

function toWireShape({ _caseId, ...caseEmail }: ICaseEmailSeed): ICaseEmail {
  return caseEmail
}

export async function getCaseEmailDataById(
  id: TEntityId
): Promise<ICaseEmail | undefined> {
  const list = await getCaseEmailListData()
  const found = list?.find((caseEmail) => caseEmail.id === id)
  return found ? toWireShape(found) : undefined
}

/**
 * Every email on a case, in the order the overview returns them.
 *
 * ⚠ Includes the ones whose `body` is null. The server's overview does, so the
 * mock must too -- otherwise the "an auto-reply must not appear on the card"
 * case cannot occur locally and the filtering that prevents it is untested.
 */
export async function getCaseEmailListDataByCaseId(
  caseId: TEntityId
): Promise<ICaseEmail[]> {
  const fn = `${mn} getCaseEmailListDataByCaseId |`
  const list = await getCaseEmailListData()
  if (!list) {
    throw new Error(`${fn} returned undefined/null`)
  }
  return list
    .filter((caseEmail) => caseEmail._caseId === caseId)
    .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
    .map(toWireShape)
}

/**
 * Record an outbound reply's envelope, in `queued`.
 *
 * ⚠ It STAYS queued. Nothing in the mock sends, exactly as nothing in the real
 * POST /replies sends -- that endpoint writes a queued message and returns it,
 * and a worker does the rest. A mock that advanced this to `sent` would
 * be making a promise the server does not make, and would quietly remove the
 * only local way to see a reply sitting unsent.
 *
 * Lives here rather than in the cases handler so `_caseId` stays inside the one
 * module that knows it is mock bookkeeping and not part of the wire shape.
 */
export async function appendOutboundReplyEmail({
  caseId,
  body,
  sentBy,
  subject,
  toAddresses,
  ccAddresses,
  bccAddresses,
  inbound,
}: {
  caseId: TEntityId
  // As the user wrote it; the server stores exactly what was sent.
  body: string
  // The agent who wrote it.
  sentBy: ICaseEmail['sentBy']
  subject: string
  toAddresses: string[]
  ccAddresses: string[]
  bccAddresses: string[]
  // What it replies to, for threading -- none on a case opened in the app.
  inbound: ICaseEmail | undefined
}): Promise<ICaseEmail> {
  const list = await getCaseEmailListData()
  const serial = String(list.length + 1).padStart(4, '0')
  const now = new Date().toISOString()
  const seed: ICaseEmailSeed = {
    _caseId: caseId,
    id: `XXCAE${serial}`,
    direction: 'outbound',
    body,
    sentBy,
    repeatOf: null,
    deliveryState: 'queued',
    // As the user sent it (prefilled "Re: <case descr>"). ⚠ Never from the
    // inbound message's subject: the most recent inbound on a case is often an
    // AUTO-REPLY, and a reply then went out as "Re: Automatic reply: ...".
    subject,
    fromAddress: 'cases@wxtechnologygroup.com',
    fromName: 'WX Technology Group Support',
    toAddresses,
    ccAddresses: ccAddresses.length ? ccAddresses : null,
    bccAddresses: bccAddresses.length ? bccAddresses : null,
    replyTo: 'cases@wxtechnologygroup.com',
    sentAt: null,
    receivedAt: null,
    createdAt: now,
    messageId: null,
    inReplyTo: inbound?.messageId ?? null,
    messageReferences: inbound?.messageId ? [inbound.messageId] : null,
    providerMessageId: null,
    mailboxHash: `case-${caseId}`,
    spamScore: null,
    // Outbound: no read status (cynque-api-server #1814).
    readStatus: null,
  }
  list.push(seed)
  await _storeEntityDataListToLocalForage({
    // storageKey IS the entity key base -- see getListDataFunctionFactory.
    entityKeyBase: STORAGE_KEY,
    listData: list as never,
    callingFn: `${mn} appendOutboundReplyEmail |`,
  })
  return toWireShape(seed)
}

/** The most recent inbound message, or undefined if the case has none. */
export async function getLatestInboundCaseEmail(
  caseId: TEntityId
): Promise<ICaseEmail | undefined> {
  const emails = await getCaseEmailListDataByCaseId(caseId)
  const inbound = emails.filter((email) => email.direction === 'inbound')
  return inbound[inbound.length - 1]
}

/**
 * Move every email on `fromCaseId` to `toCaseId` -- the mock half of a case
 * merge (POST /api/cases/:id/merges). Here, like appendOutboundReplyEmail, so
 * `_caseId` stays inside the module that knows it is mock bookkeeping.
 */
export async function moveCaseEmailsToCase({
  fromCaseId,
  toCaseId,
}: {
  fromCaseId: TEntityId
  toCaseId: TEntityId
}): Promise<number> {
  const list = await getCaseEmailListData()
  const moving = list.filter((caseEmail) => caseEmail._caseId === fromCaseId)
  for (const caseEmail of moving) {
    caseEmail._caseId = toCaseId
  }
  if (moving.length) {
    await _storeEntityDataListToLocalForage({
      entityKeyBase: STORAGE_KEY,
      listData: list as never,
      callingFn: `${mn} moveCaseEmailsToCase |`,
    })
  }
  return moving.length
}

/**
 * PATCH /api/emails/{id}/readStatus, for a case email (cynque-api-server
 * #1814). `undefined` when no case email has the id -- the caller then looks
 * among the activity emails -- otherwise the refusal or the updated email.
 */
export async function setCaseEmailReadStatus(
  id: TEntityId,
  toId: unknown
): Promise<{ refusal: string } | { caseEmail: ICaseEmail } | undefined> {
  const list = await getCaseEmailListData()
  const found = list.find((caseEmail) => caseEmail.id === id)
  if (!found) {
    return undefined
  }
  const refusal = readStatusChangeRefusal(found, toId)
  if (refusal) {
    return { refusal }
  }
  found.readStatus = readStatusItem(toId as number)
  await storeCaseEmails(list, `${mn} setCaseEmailReadStatus |`)
  return { caseEmail: toWireShape(found) }
}

/**
 * Why a reply's `inReplyToEmailId` is refused on this case, or null -- see
 * inReplyToRefusal.
 */
export async function caseReplyTargetRefusal(
  caseId: TEntityId,
  emailId: TEntityId
): Promise<string | null> {
  const list = await getCaseEmailListData()
  const found = list.find((caseEmail) => caseEmail.id === emailId)
  return inReplyToRefusal(
    found && { caseId: found._caseId, direction: found.direction },
    caseId
  )
}

/**
 * A reply went to this message: it becomes Replied, whatever it was. The
 * server does this in the reply's own transaction (cynque-api-server #1814).
 */
export async function markCaseEmailReplied(id: TEntityId): Promise<void> {
  const list = await getCaseEmailListData()
  const found = list.find((caseEmail) => caseEmail.id === id)
  if (!found || found.direction !== 'inbound') {
    return
  }
  found.readStatus = readStatusItem(READ_STATUS.REPLIED)
  await storeCaseEmails(list, `${mn} markCaseEmailReplied |`)
}
